import { createAsyncThunk, createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IFsNodeSchema } from '@cloudike/web_files'
import { NOTIFICATION_TYPES, showNotification } from 'features/common/notifications'
import { LOADING_STATUSES } from 'shared/constants/loadingStatuses'
import { getFilesSdk } from 'shared/sdk/files'
import { fetchAllBoardsThunk } from 'features/boards/model/slice/boardsSlice'
import { t } from 'i18next'
import { assetsApi } from 'shared/api/assetsApi'
import { RootState } from 'shared/config/store'

import { RecentlyDeletedState } from '../types/recentlyDeleted'

export enum RecentlyDeletedToolbarAction {
  Recover = 'recover',
  Delete = 'delete',
}

const adapter = createEntityAdapter<IFsNodeSchema>()

export const recentlyDeletedSelectors = adapter.getSelectors()

const initialState: RecentlyDeletedState = {
  status: LOADING_STATUSES.LOADING,
  error: null,
  allBoards: [],
  areAllTrashedNodesFetched: false,
}

let paginator: any = null

export const fetchFirstTrashedNodesThunk = createAsyncThunk(
  'trash/fetchFirstTrashedNodes',
  async (_, { dispatch, getState }) => {
    const sdk = getFilesSdk()
    const { familyTrashBin } = sdk

    const params = {
      preview: true,
      preview_jwt: true,
      embedded: true,
      explicitly_trashed_only: true,
    }

    paginator = familyTrashBin.getTrashItemsPaginator(40, params)
    const response = await paginator.next()

    const state = getState() as { recentlyDeleted: RecentlyDeletedState }
    const allBoards = state.recentlyDeleted.allBoards || []

    const boardInfoMap = new Map(allBoards.map(board => [board.id, { name: board.name, is_trashed: board.is_trashed }]))

    const enrichedNodes = (response?.data?._embedded?.nodes || []).map(node => {
      const parentInfo = boardInfoMap.get(node.parent_id)
      return {
        ...node,
        parent_name: parentInfo?.name || null,
        is_parent_trashed: parentInfo?.is_trashed ?? null,
      }
    })

    dispatch(actions.setAllTrashedNodes(enrichedNodes))
    dispatch(actions.setAreAllTrashedNodesFetched(!response?.data?._links?.next))
  }
)

export const fetchNextTrashedNodesThunk = createAsyncThunk(
  'trash/fetchNextTrashedNodes',
  async (_, { dispatch, getState }) => {
    if (!paginator) return

    const response = await paginator.next()

    const state = getState() as { recentlyDeleted: RecentlyDeletedState }
    const allBoards = state.recentlyDeleted.allBoards || []

    const boardInfoMap = new Map(allBoards.map(board => [board.id, { name: board.name, is_trashed: board.is_trashed }]))

    const enrichedNodes = (response?.data?._embedded?.nodes || []).map(node => {
      const parentInfo = boardInfoMap.get(node.parent_id)
      return {
        ...node,
        parent_name: parentInfo?.name || null,
        is_parent_trashed: parentInfo?.is_trashed ?? null,
      }
    })

    dispatch(actions.addTrashedNodes(enrichedNodes))
    dispatch(actions.setAreAllTrashedNodesFetched(!response?.data?._links?.next))
  }
)

export const fetchBoardsThunk = createAsyncThunk(
  "boards/fetchBoardsThunk",
  async (_, { dispatch }) => {
    const sdk = getFilesSdk()
    const { boardsService } = sdk
    const nodesOptions = { embedded: true, preview_jwt: true, preview: true, type__in: ['dir'] }
    const delay = 100
    const pageSize = 500
    const allBoards = await boardsService.getAllBoards(
      nodesOptions, 
      pageSize, 
      delay
    )
    dispatch(actions.setAllBoards(allBoards))
  }
)

export const fetchAllBoardsAndTrashedNodesThunk = createAsyncThunk(
  'trash/fetchAllBoardsAndTrashedNodes',
  async (_, { dispatch }) => {
    await dispatch(fetchBoardsThunk())
    await dispatch(fetchFirstTrashedNodesThunk())
  }
)

export const emptyTrashThunk = createAsyncThunk(
  'trash/emptyTrash',
  async ({ notificationKey }: { notificationKey: string }) => {
    const sdk = getFilesSdk()
    const { familyTrashBin } = sdk

    try {
      await familyTrashBin.emptyTrash()
      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: 'Trash emptied',
        message: 'Trash emptied successfully',
        key: notificationKey,
      })
    } catch (error) {
      showNotification({
        type: NOTIFICATION_TYPES.ERROR,
        title: 'Failed to empty trash',
        message: 'Please try again',
        key: notificationKey,
      })
      throw error
    }
  }
)

export const restoreNodesThunk = createAsyncThunk(
  'trash/restoreNodes',
  async ({ nodeIds, nodeType, successfullCallback, notificationKey }: { nodeIds: string[]; nodeType: string, successfullCallback: (id: string) => void, notificationKey: string }, { dispatch, getState }) => {
    const state = getState() as RootState
    const userId = state.user.user?.family_user_id

    let successfullCount = 0
    let anotherFailedCount = 0
    let failedBecauseOfUniqueLinkCount = 0

    for (const id of nodeIds) {
      try {
        await assetsApi.updateAsset(userId, id, { unique_link: true, is_trashed: false })
        successfullCount++

        successfullCallback(id)
      } catch (error) {
        const isUniqueError = error?.detail?.is_trashed === 'Node with the same linked_node_id already exists in the parent directory'

        if (isUniqueError) {
          failedBecauseOfUniqueLinkCount++

          continue
        }

        anotherFailedCount++
      }
    }

    if (nodeType === 'Board') {
      await dispatch(fetchAllBoardsThunk())
    }

    if (successfullCount > 0) {
      if (failedBecauseOfUniqueLinkCount === 0 && anotherFailedCount === 0) {
        showNotification({
          type: NOTIFICATION_TYPES.SUCCESS,
          title: t('l_notification_assetsRestored', { number: successfullCount }),
          message: '',
          key: notificationKey,
        })
      } else if (failedBecauseOfUniqueLinkCount > 0) {
        showNotification({
          type: NOTIFICATION_TYPES.SUCCESS,
          title: t('l_notification_assetsRestoredMix', { number: successfullCount, added: successfullCount, notAdded: failedBecauseOfUniqueLinkCount }),
          message: '',
          key: notificationKey,
        })
      }

      return
    }

    if (failedBecauseOfUniqueLinkCount > 0) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: t('l_notification_assetsAlreadyOnBoard', { number: failedBecauseOfUniqueLinkCount }),
        message: '',
        key: notificationKey,
      })

      return
    }

    if (anotherFailedCount > 0) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Something went wrong',
        message: 'Please try again',
        key: notificationKey,
      })
    }
  }
)

export const deleteNodesThunk = createAsyncThunk(
  'trash/deleteNodes',
  async ({ nodeIds, notificationKey }: { nodeIds: string[]; notificationKey: string }) => {
    const sdk = getFilesSdk()
    const { familyTrashBin } = sdk

    try {
      await familyTrashBin.deleteItems(nodeIds)
      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: 'Deleted successfully',
        message: 'Item was successfully deleted',
        key: notificationKey,
      })
    } catch (error) {
      showNotification({
        type: NOTIFICATION_TYPES.ERROR,
        title: 'Delete failed',
        message: 'Something went wrong. Please try again.',
        key: notificationKey,
      })
      throw error
    }
  }
)

export const fullRestoreThunk = createAsyncThunk(
  'trash/fullRestore',
  async ({ notificationKey }: { notificationKey: string }) => {
    const sdk = getFilesSdk()
    const { familyTrashBin } = sdk

    try {
      await familyTrashBin.fullRestore()
      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: 'All items recovered',
        message: 'All items recovered successfully',
        key: notificationKey,
      })
    } catch (error) {
      showNotification({
        type: NOTIFICATION_TYPES.ERROR,
        title: 'Recover failed',
        message: 'Please try again',
        key: notificationKey,
      })
    }
  }
)

export const recentlyDeletedSlice = createSlice({
  name: 'recentlyDeleted',
  initialState: adapter.getInitialState(initialState),
  reducers: {
    setStatus: (state, action) => {
      state.status = action.payload
    },
    setAllBoards: (state, action) => {
      state.allBoards = action.payload
    },
    setAllTrashedNodes: (state, action) => {
      adapter.setAll(state, action.payload)
    },
    addTrashedNodes: (state, action) => {
      adapter.addMany(state, action.payload)
    },
    removeNode: (state, action: PayloadAction<string>) => {
      adapter.removeOne(state, action.payload)
    },
    removeMultipleNodes: (state, action: PayloadAction<string[]>) => {
      adapter.removeMany(state, action.payload)
    },
    setAreAllTrashedNodesFetched: (state, action) => {
      state.areAllTrashedNodesFetched = action.payload
    },
    resetState: (state) => {
      adapter.removeAll(state)
      state.status = LOADING_STATUSES.LOADING
      state.error = null
      state.allBoards = []
      state.areAllTrashedNodesFetched = false
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFirstTrashedNodesThunk.pending, (state) => {
        if (state.ids.length === 0) {
          state.status = LOADING_STATUSES.LOADING
        }
      })
      .addCase(fetchFirstTrashedNodesThunk.fulfilled, (state) => {
        state.status = LOADING_STATUSES.SUCCEEDED
      })
      .addCase(fetchFirstTrashedNodesThunk.rejected, (state) => {
        state.status = LOADING_STATUSES.FAILED
      })
  },
})

const { 
  reducer, actions 
} = recentlyDeletedSlice

export { actions as recentlyDeletedActions, reducer as recentlyDeletedReducer }
